import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  StudentwellbeingService,
  FeedbackDetailsResponse,
  FeedbackHistoryEntry,
  FeedbackHistoryOption,
  FeedbackHistoryQuestionOption,
  StudentSearchResult,
  StudentSearchCacheItem,
} from '../studentwellbeing.service';
import { WarningAlertComponent } from 'src/app/warning-alert/warning-alert.component';


export interface AdaptabilityOption {
  id: string;
  label: string;
  labelTe: string;
  isText?: boolean;
  optionRank?: number;
}

export interface AdaptabilityQuestion {
  id: string;
  text: string;
  textTe: string;
  questionType: 'SINGLE' | 'MULTIPLE';
  options: AdaptabilityOption[];
}


@Component({
  selector: 'app-student-form',
  templateUrl: './student-form.component.html',
  styleUrls: ['./student-form.component.scss']
})
export class StudentFormComponent implements OnInit, OnDestroy {

  searchCriteria = '';
  searchResults: StudentSearchResult[] = [];
  recentSearchList: StudentSearchCacheItem[] = [];
  showSearchList = false;
  searchLoading = false;
  searchNoResults = false;
  selectedAdmNo = '';
  selectedUniqueRefId = '';
  selectedStudentLabel = '';
  selectedStudent: StudentSearchResult | null = null;
  studentSelected = false;
  private readonly minSearchLength = 3;
  private searchRequestSeq = 0;

  /** 'en' = English, 'te' = Telugu */
  currentLang: 'en' | 'te' = 'en';
  langDropdownOpen = false;
  readonly languageOptions: { value: 'en' | 'te'; label: string }[] = [
    { value: 'en', label: 'English' },
    { value: 'te', label: 'తెలుగు' },
  ];
  questions: AdaptabilityQuestion[] = [];
  loading = true;
  errorMessage = '';
  /** Shown when user clicks Submit without answering all questions */
  submitValidationMessage = '';

  /** SINGLE: questionId -> optionId. MULTIPLE: questionId -> optionId[] */
  answersSingle: Record<string, string> = {};
  answersMultiple: Record<string, string[]> = {};
  /** For options with optionName "TEXT": questionId -> text (max 1000 characters) */
  answersText: Record<string, string> = {};
  readonly maxChars = 150;

  /** From feedback details: questionId -> optionId -> feedbackId (for submit payload). */
  feedbackIdMap: Record<string, Record<string, number>> = {};

  /** Full options list from getDetails per question (for MULTIPLE: submit all with status true/false). */
  detailsOptionsByQuestion: Record<string, { optionId: number; feedbackId: number }[]> = {};

  /** From feedback details API (used in submit payload). */
  detailsAdmNo = '';
  detailsSectionId = 0;
  detailsFeedBackType = 'STUDENT_FEEDBACK';

  categoryName = 'STUDENT WELL BEING';
  feedBackName = 'STUDENT_FEEDBACK';
  submitting = false;
  submitSuccessMessage = '';
  submitErrorMessage = '';
  selectedSectionId: any;

  showHistoryView = false;
  feedbackHistory: FeedbackHistoryEntry[] = [];
  selectedHistoryDateIndex = 0;
  historyLoading = false;
  studentImageUrl = '';
  hasStudentImage = false;

  constructor(
    private adaptabilityService: StudentwellbeingService,
    private spinner: NgxSpinnerService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    // Questions load after a student is selected from search.
    this.loadRecentSearchList();
  }

  ngOnDestroy(): void {
    this.revokeStudentImageUrl();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target && !target.closest('.student-search')) {
      this.closeSearchList();
    }
    if (target && !target.closest('.adaptability__lang-select')) {
      this.langDropdownOpen = false;
    }
  }

  get shouldShowSearchDropdown(): boolean {
    return this.showSearchList && (this.shouldShowSearchResults || this.shouldShowRecentList);
  }

  get shouldShowSearchResults(): boolean {
    return this.searchResults.length > 0;
  }

  get shouldShowRecentList(): boolean {
    return (
      this.recentSearchList.length > 0 &&
      this.searchResults.length === 0 &&
      !this.searchLoading
    );
  }

  /** Show questionnaire only when a student is picked from search and query matches their adm no. */
  get showAdaptabilitySection(): boolean {
    const query = this.searchCriteria.trim();
    return (
      this.studentSelected &&
      !!this.selectedAdmNo &&
      query.length > 0 &&
      query === this.selectedAdmNo &&
      !this.searchNoResults
    );
  }

  get showSelectedStudentCard(): boolean {
    return this.showAdaptabilitySection;
  }

  get selectedDisplayName(): string {
    return String(this.selectedStudent?.name ?? this.selectedStudent?.studentName ?? '').trim();
  }

  get selectedDisplayCampus(): string {
    return String(this.selectedStudent?.campus ?? this.selectedStudent?.campusName ?? '').trim();
  }

  get selectedDisplayStudentType(): string {
    return String(this.selectedStudent?.studentType ?? '').trim();
  }
  get selectedDisplayParentName(): string {
    return String(this.selectedStudent?.parentName ?? '').trim();
  }

  clearSelectedStudent(): void {
    this.searchCriteria = '';
    this.closeSearchList();
    this.clearStudentSelection();
  }

  onSearchCriteriaChange(value: string): void {
    if (!String(value ?? '').trim()) {
      this.closeSearchList();
      this.clearStudentSelection();
      return;
    }
    this.syncStudentSelectionWithSearch();
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value ?? '';
    this.searchCriteria = value;
    if (!value.trim()) {
      this.searchResults = [];
      this.searchNoResults = false;
      this.showSearchList = this.recentSearchList.length > 0;
      this.clearStudentSelection();
      return;
    }
    if (value.trim().length < this.minSearchLength) {
      this.searchResults = [];
      this.searchNoResults = false;
      this.showSearchList = this.recentSearchList.length > 0;
      this.syncStudentSelectionWithSearch();
      return;
    }
    this.syncStudentSelectionWithSearch();
    this.searchNoResults = false;
    this.searchResults = [];
    this.showSearchList = this.recentSearchList.length > 0;
  }

  onSearchBoxFocus(): void {
    if (this.searchResults.length > 0 || this.recentSearchList.length > 0) {
      this.showSearchList = true;
    }
    if (!this.recentSearchList.length) {
      this.loadRecentSearchList();
    }
  }

  onSearchIconClick(event: MouseEvent): void {
    event.stopPropagation();
    this.triggerStudentSearch();
  }

  private triggerStudentSearch(): void {
    const criteria = this.searchCriteria.trim();
    if (criteria.length < this.minSearchLength) {
      return;
    }
    this.runStudentSearch(criteria);
  }

  private loadRecentSearchList(): void {
    this.spinner.show();
    this.adaptabilityService.searchCacheDetails('STUDENT').subscribe({
      next: (list) => {
        this.recentSearchList = Array.isArray(list) ? list : [];
        this.spinner.hide();
      },
      error: () => {
        this.recentSearchList = [];
        this.spinner.hide();
      },
    });
  }

  /** Hide form when search text changes away from the selected student's adm no. */
  private syncStudentSelectionWithSearch(): void {
    const query = this.searchCriteria.trim();
    if (!query || !this.selectedAdmNo) {
      return;
    }
    if (query !== this.selectedAdmNo) {
      this.clearStudentSelection();
    }
  }

  private closeSearchList(): void {
    this.searchRequestSeq++;
    this.showSearchList = false;
    this.searchResults = [];
    this.searchNoResults = false;
    this.searchLoading = false;
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.triggerStudentSearch();
    }
  }

  selectCachedStudent(item: StudentSearchCacheItem): void {
    this.selectStudent(this.mapCacheItemToSearchResult(item));
  }

  private mapCacheItemToSearchResult(item: StudentSearchCacheItem): StudentSearchResult {
    const name = String(item.searchName ?? item.searchKey ?? '').trim();
    const admNo = String(item.searchSelected ?? '').trim();
    return {
      admNo,
      name,
      studentName: name,
      searchSelected: admNo,
      parentName: String(item.parentName ?? '').trim(),
      stdUniqueRefId: item.stdUniqueRefId,
      uniqueRefId: item.stdUniqueRefId,
      businessType: item.businessType,
    };
  }

  private runStudentSearch(criteria: string): void {
    const requestSeq = ++this.searchRequestSeq;
    this.searchLoading = true;
    this.showSearchList = true;
    this.spinner.show();
    this.adaptabilityService.studentSearch(criteria).subscribe({
      next: (res) => {
        if (requestSeq !== this.searchRequestSeq) {
          return;
        }
        if (this.searchCriteria.trim() !== criteria) {
          return;
        }
        if (this.searchCriteria.trim().length < this.minSearchLength) {
          this.closeSearchList();
          return;
        }
        this.searchLoading = false;
        const list = Array.isArray(res) ? res : [];
        this.searchResults = list;
        this.showSearchList = list.length > 0;
        this.searchNoResults = list.length === 0;
        if (this.searchNoResults) {
          this.clearStudentSelection();
        }
        this.spinner.hide();
      },
      error: () => {
        if (requestSeq !== this.searchRequestSeq) {
          return;
        }
        this.searchLoading = false;
        this.searchResults = [];
        this.showSearchList = false;
        this.searchNoResults = this.searchCriteria.trim().length >= this.minSearchLength;
        if (this.searchNoResults) {
          this.clearStudentSelection();
        }
        this.spinner.hide();
      },
    });
  }

  selectStudent(student: StudentSearchResult): void {
    const admNo = String(student.admNo ?? student.searchSelected ?? '').trim();
    const uniqueRefId = student.uniqueRefId ?? student.stdUniqueRefId ?? '';
    if (!admNo) {
      return;
    }
    this.selectedSectionId = student.sectionId ?? 0;
    this.selectedAdmNo = admNo;
    this.selectedUniqueRefId = String(uniqueRefId);
    this.selectedStudent = student;
    this.searchCriteria = admNo;
    this.selectedStudentLabel = this.formatStudentLabel(student);
    this.closeSearchList();
    this.searchNoResults = false;
    this.studentSelected = true;

    localStorage.setItem('admNo', this.selectedAdmNo);
    localStorage.setItem('uniqueRefId', this.selectedUniqueRefId);

    this.saveStudentSearchCache(student);
    this.loadStudentImage();
    this.resetFormState();
    this.loadQuestions();
  }

  /** Persist selection to recent-search cache (same API as other student search screens). */
  private saveStudentSearchCache(student: StudentSearchResult): void {
    const searchSelected = String(student.admNo ?? student.searchSelected ?? '').trim();
    const searchKey = String(student.name ?? student.studentName ?? '').trim();
    if (!searchSelected || !searchKey) {
      return;
    }
    this.adaptabilityService.saveSearchCache({
      searchKey,
      searchSelected,
      searchType: 'Student',
      favourite: false,
      businessType: student.businessType ?? 'COLLEGE',
      parentName: String(student.parentName ?? '').trim(),
    }).subscribe({
      next: () => this.loadRecentSearchList(),
      error: () => {
        // Do not block student selection if cache save fails.
      },
    });
  }

  private formatStudentLabel(student: StudentSearchResult): string {
    const name = String(student.name ?? student.studentName ?? '').trim();
    const admNo = String(student.admNo ?? student.searchSelected ?? '').trim();
    const mobile = student.mobileNo != null && String(student.mobileNo).trim() !== ''
      ? String(student.mobileNo).trim()
      : '';
    const campus = String(student.campus ?? student.campusName ?? '').trim();
    const studentType = String(student.studentType ?? '').trim();
    const parts = [name, admNo, campus, studentType].filter(Boolean);
    return parts.join(' · ');
  }

  private clearStudentSelection(): void {
    this.studentSelected = false;
    this.selectedAdmNo = '';
    this.selectedUniqueRefId = '';
    this.selectedStudentLabel = '';
    this.selectedStudent = null;
    this.selectedSectionId = 0;
    this.revokeStudentImageUrl();
    this.hasStudentImage = false;
    this.resetFormState();
    this.loading = false;
  }

  private loadStudentImage(): void {
    const admNoForImage = this.selectedUniqueRefId || this.selectedAdmNo;
    if (!admNoForImage) {
      this.revokeStudentImageUrl();
      this.hasStudentImage = false;
      return;
    }

    this.adaptabilityService.getStudentUploadImage(String(admNoForImage)).subscribe({
      next: (res) => {
        if (res.status === 204) {
          this.revokeStudentImageUrl();
          this.hasStudentImage = false;
          return;
        }
        if (res.status === 200 && res.body && res.body.size > 0) {
          this.revokeStudentImageUrl();
          this.studentImageUrl = URL.createObjectURL(res.body);
          this.hasStudentImage = true;
          return;
        }
        this.revokeStudentImageUrl();
        this.hasStudentImage = false;
      },
      error: () => {
        this.revokeStudentImageUrl();
        this.hasStudentImage = false;
      }
    });
  }

  private revokeStudentImageUrl(): void {
    if (this.studentImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.studentImageUrl);
    }
    this.studentImageUrl = '';
  }

  private resetFormState(): void {
    this.questions = [];
    this.answersSingle = {};
    this.answersMultiple = {};
    this.answersText = {};
    this.feedbackIdMap = {};
    this.detailsOptionsByQuestion = {};
    this.detailsAdmNo = '';
    this.detailsSectionId = 0;
    this.submitValidationMessage = '';
    this.submitSuccessMessage = '';
    this.submitErrorMessage = '';
    this.loading = true;
    this.errorMessage = '';
    this.showHistoryView = false;
    this.feedbackHistory = [];
    this.selectedHistoryDateIndex = 0;
    this.historyLoading = false;
  }

  loadQuestions(): void {
    this.loading = true;
    this.errorMessage = '';
    this.spinner.show();
    this.adaptabilityService.getQuestionsWithOptions(this.categoryName).subscribe({
      next: (questions) => {
        this.questions = questions;
        this.spinner.hide();
        this.loadFeedbackDetails();
      },
      error: (err: any) => {
        this.errorMessage = err?.message || 'Failed to load questions';
        this.loading = false;
        this.spinner.hide();
      }
    });
  }

  /**
   * Call feedback details API first and pre-fill form from response.
   */
  private loadFeedbackDetails(): void {
    this.spinner.show();
    this.adaptabilityService.getFeedbackDetails(this.selectedAdmNo, this.categoryName).subscribe({
      next: (details: FeedbackDetailsResponse | null) => {
        if (details != null) {
          this.detailsAdmNo = details.admNO ?? '';
          this.detailsSectionId = details.sectionId ?? 0;
          this.detailsFeedBackType = details.feedBackType ?? 'STUDENT_FEEDBACK';
          this.applyDetailsToForm(details);
        }
        this.loading = false;
        this.spinner.hide();
      },
      error: () => {
        this.loading = false;
        this.spinner.hide();
      }
    });
  }

  private applyDetailsToForm(details: FeedbackDetailsResponse): void {
    if (!details.questionAndOption || !this.questions.length) return;
    this.detailsOptionsByQuestion = {};
    const byQuestionId = new Map<string, { optionId: number; feedbackId: number; feedbackOptionText: string; status: boolean }[]>();
    for (const qa of details.questionAndOption) {
      const opts: { optionId: number; feedbackId: number; feedbackOptionText: string; status: boolean }[] = [];
      for (const opt of qa.options || []) {
        opts.push({
          optionId: opt.optionId,
          feedbackId: opt.feedbackId,
          feedbackOptionText: opt.feedbackOptionText || '',
          status: opt.status
        });
      }
      if (opts.length) byQuestionId.set(String(qa.questionId), opts);
    }
    for (const q of this.questions) {
      const opts = byQuestionId.get(q.id) ?? byQuestionId.get(String(Number(q.id)));
      if (!opts || opts.length === 0) continue;
      const selectedOpts = opts.filter(o => o.status);
      this.detailsOptionsByQuestion[q.id] = opts.map(o => ({ optionId: o.optionId, feedbackId: o.feedbackId }));
      const hasTextOption = q.options.some(o => o.isText);
      if (hasTextOption && opts.length === 1) {
        this.answersText[q.id] = opts[0].feedbackOptionText;
        if (!this.feedbackIdMap[q.id]) this.feedbackIdMap[q.id] = {};
        this.feedbackIdMap[q.id][String(opts[0].optionId)] = opts[0].feedbackId;
      } else if (q.questionType === 'SINGLE' && selectedOpts.length >= 1) {
        const first = selectedOpts[0];
        this.answersSingle[q.id] = String(first.optionId);
        if (!this.feedbackIdMap[q.id]) this.feedbackIdMap[q.id] = {};
        opts.forEach(o => { this.feedbackIdMap[q.id][String(o.optionId)] = o.feedbackId; });
      } else if (q.questionType === 'MULTIPLE') {
        this.answersMultiple[q.id] = selectedOpts.map(o => String(o.optionId));
        if (!this.feedbackIdMap[q.id]) this.feedbackIdMap[q.id] = {};
        opts.forEach(o => { this.feedbackIdMap[q.id][String(o.optionId)] = o.feedbackId; });
      } else if (hasTextOption && opts.length === 1) {
        if (!this.feedbackIdMap[q.id]) this.feedbackIdMap[q.id] = {};
        this.feedbackIdMap[q.id][String(opts[0].optionId)] = opts[0].feedbackId;
      }
    }
  }

  get answeredCount(): number {
    let count = 0;
    for (const q of this.questions) {
      const hasTextOption = q.options.some(o => o.isText);
      const onlyText = hasTextOption && q.options.every(o => o.isText);
      if (onlyText) {
        if ((this.answersText[q.id] || '').trim().length > 0) count++;
      } else if (hasTextOption) {
        const textFilled = (this.answersText[q.id] || '').trim().length > 0;
        const singleFilled = !!this.answersSingle[q.id];
        const multiFilled = (this.answersMultiple[q.id] || []).length > 0;
        if (textFilled || singleFilled || multiFilled) count++;
      } else if (q.questionType === 'SINGLE' && this.answersSingle[q.id]) count++;
      else if (q.questionType === 'MULTIPLE') {
        const arr = this.answersMultiple[q.id];
        if (arr && arr.length > 0) count++;
      }
    }
    return count;
  }

  getQuestionText(q: AdaptabilityQuestion): string {
    return this.currentLang === 'te' ? q.textTe : q.text;
  }

  getOptionLabel(opt: AdaptabilityOption): string {
    return this.currentLang === 'te' ? opt.labelTe : opt.label;
  }

  /** Whether this question has any option with optionName "TEXT" (show textarea). */
  hasTextOption(q: AdaptabilityQuestion): boolean {
    return q.options.some(o => o.isText);
  }

  /** Whether all options are TEXT (no radio/checkbox list). */
  onlyTextOptions(q: AdaptabilityQuestion): boolean {
    return q.options.length > 0 && q.options.every(o => o.isText);
  }

  /** Whether to show SINGLE (radio) options (has non-TEXT options). */
  showSingleOptions(q: AdaptabilityQuestion): boolean {
    return q.questionType === 'SINGLE' && !this.onlyTextOptions(q);
  }

  /** Whether to show MULTIPLE (checkbox) options (has non-TEXT options). */
  showMultipleOptions(q: AdaptabilityQuestion): boolean {
    return q.questionType === 'MULTIPLE' && !this.onlyTextOptions(q);
  }

  isOptionText(opt: AdaptabilityOption): boolean {
    return !!opt.isText;
  }

  /** Options sorted by optionRank (greater/higher first) for display. */
  getOptionsByRank(opts: AdaptabilityOption[]): AdaptabilityOption[] {
    return [...opts].sort((a, b) => (b.optionRank ?? 0) - (a.optionRank ?? 0));
  }


  get languageLabel(): string {
    return this.currentLang === 'te' ? 'తెలుగు' : 'English';
  }

  setLanguage(lang: 'en' | 'te'): void {
    this.currentLang = lang;
  }

  toggleLangDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.langDropdownOpen = !this.langDropdownOpen;
  }

  selectLanguage(lang: 'en' | 'te'): void {
    this.setLanguage(lang);
    this.langDropdownOpen = false;
  }

  onCheckHistory(): void {
    if (this.showHistoryView) {
      this.showHistoryView = false;
      return;
    }
    this.loadFeedbackHistory();
  }

  loadFeedbackHistory(): void {
    if (!this.selectedAdmNo) {
      return;
    }
    this.historyLoading = true;
    this.spinner.show();
    this.adaptabilityService.getFeedbackHistory(this.selectedAdmNo, this.categoryName).subscribe({
      next: (data) => {
        const list = Array.isArray(data) ? data : [];
        this.feedbackHistory = [...list].sort(
          (a, b) => b.feedBackDate.localeCompare(a.feedBackDate)
        );
        this.selectedHistoryDateIndex = 0;
        this.showHistoryView = true;
        this.historyLoading = false;
        this.spinner.hide();
      },
      error: () => {
        this.historyLoading = false;
        this.spinner.hide();
        this.dialog.open(WarningAlertComponent, {
          width: '400px',
          height: '60px',
          position: { top: '10px', right: '10px' },
          data: {
            message: 'Failed to load feedback history.',
            status: false,
          },
        });
      },
    });
  }

  selectHistoryDate(index: number): void {
    this.selectedHistoryDateIndex = index;
  }

  formatHistoryDate(isoDate: string): string {
    const parts = String(isoDate ?? '').split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return isoDate;
  }

  get historyFeedbackCountLabel(): string {
    const n = this.feedbackHistory.length;
    if (n === 1) {
      return 'Last 1 feedback';
    }
    return `Last ${n} feedback`;
  }

  get selectedHistoryEntry(): FeedbackHistoryEntry | null {
    return this.feedbackHistory[this.selectedHistoryDateIndex] ?? null;
  }

  getSelectedHistoryQuestions(): FeedbackHistoryQuestionOption[] {
    return this.selectedHistoryEntry?.questionAndOption ?? [];
  }

  getSelectedHistoryOptions(q: FeedbackHistoryQuestionOption): FeedbackHistoryOption[] {
    return (q.options ?? []).filter(o => o.status);
  }

  getHistoryOptionLabel(opt: FeedbackHistoryOption): string {
    const text = (opt.feedbackOptionText ?? '').trim();
    if (text) {
      return text;
    }
    return String(opt.option ?? '').trim();
  }

  getHistoryOptionRank(opt: FeedbackHistoryOption): number {
    const rank = opt.optionRank ?? 1;
    return rank >= 1 && rank <= 5 ? rank : 1;
  }

  getHistoryRankStyles(rank: number): { border: string; background: string; circle: string } {
    const palette: Record<number, { border: string; background: string; circle: string }> = {
      1: { border: '#dc2626', background: 'rgba(220, 38, 38, 0.08)', circle: '#dc2626' },
      2: { border: '#eab308', background: 'rgba(234, 179, 8, 0.14)', circle: '#ca8a04' },
      3: { border: '#f97316', background: 'rgba(249, 115, 22, 0.1)', circle: '#f97316' },
      4: { border: '#0ea5e9', background: 'rgba(14, 165, 233, 0.1)', circle: '#0ea5e9' },
      5: { border: '#22c55e', background: 'rgba(34, 197, 94, 0.1)', circle: '#16a34a' },
    };
    return palette[rank] ?? palette[1];
  }

  onTextChange(questionId: string, value: string): void {
    if (value.length <= this.maxChars) {
      this.answersText[questionId] = value;
    } else {
      this.answersText[questionId] = value.slice(0, this.maxChars);
    }
    this.submitValidationMessage = '';
  }

  getTextAnswer(questionId: string): string {
    return this.answersText[questionId] || '';
  }

  getTextCharCount(questionId: string): number {
    return (this.answersText[questionId] || '').length;
  }

  get totalCount(): number {
    return this.questions.length;
  }

  onSelectSingle(questionId: string, optionId: string): void {
    this.answersSingle[questionId] = optionId;
    this.submitValidationMessage = '';
  }

  isSelectedSingle(questionId: string, optionId: string): boolean {
    return this.answersSingle[questionId] === optionId;
  }

  onToggleMultiple(questionId: string, optionId: string): void {
    const arr = this.answersMultiple[questionId] || [];
    if (arr.includes(optionId)) {
      this.answersMultiple[questionId] = arr.filter(id => id !== optionId);
    } else {
      this.answersMultiple[questionId] = [...arr, optionId];
    }
    this.submitValidationMessage = '';
  }

  isSelectedMultiple(questionId: string, optionId: string): boolean {
    const arr = this.answersMultiple[questionId] || [];
    return arr.includes(optionId);
  }

  onSubmit(): void {
    const AdmNo = this.selectedUniqueRefId || '';
    this.submitValidationMessage = '';
    this.submitSuccessMessage = '';
    this.submitErrorMessage = '';
    if (this.answeredCount < this.totalCount) {
      // this.submitValidationMessage = 'Please answer all questions before submitting.';
      this.dialog.open(WarningAlertComponent, {
        width: '400px',
        height: '60px',
        position: { top: '10px', right: '10px' },
        data: {
          message: 'Please answer all questions before submitting.',
          status: false
        }
      })

      return;
    }
    const questionAndOption = this.buildSubmitQuestionAndOption();
    const payload = {
      admNO: AdmNo ?? '',
      category: this.categoryName,
      empId: 0,
      sectionId: this.selectedSectionId ?? 0,
      questionAndOption,
      feedBackType: this.detailsFeedBackType || this.feedBackName,
      validateMessage: ''
    };
    this.submitting = true;
    this.spinner.show();
    this.adaptabilityService.submitFeedback(payload).subscribe({
      next: () => {
        this.submitting = false;
        this.submitValidationMessage = '';
        // this.submitSuccessMessage = 'Feedback submitted successfully.';
        this.loadFeedbackDetails(); // Refresh details after submit
        this.spinner.hide();
        // setTimeout(() => { this.submitSuccessMessage = ''; }, 2000);/
        this.dialog.open(WarningAlertComponent, {
          width: '400px',
          height: '60px',
          position: { top: '10px', right: '10px' },
          data: {
            message: "Feedback submitted successfully.",
            status: true

          }
        })
      },
      error: (err: any) => {
        this.submitting = false;
        // this.submitErrorMessage = err?.error?.message || err?.message || 'Failed to submit feedback.';
        this.spinner.hide();
        // setTimeout(() => { this.submitErrorMessage = ''; }, 2000);
        this.dialog.open(WarningAlertComponent, {
          width: '400px',
          height: '60px',
          position: { top: '10px', right: '10px' },
          data: {
            message: "Failed to submit feedback.",
            status: false
          }
        })
      }
    });
  }

  /** Get feedbackId for an option (from details map); always returns a number. */
  private getFeedbackId(questionId: string, optionId: string | number): number {
    const map = this.feedbackIdMap[questionId];
    if (!map) return 0;
    const id = typeof optionId === 'number' ? optionId : Number(optionId);
    return map[String(optionId)] ?? map[id] ?? 0;
  }

  private buildSubmitQuestionAndOption(): {
    questionId: number;
    options: { optionId: number; feedbackId: number; status: boolean; feedbackOptionText: string; feedbackPopUpText: string }[];
  }[] {
    return this.questions.map(q => {
      const hasTextOption = q.options.some(o => o.isText);
      const options: { optionId: number; feedbackId: number; status: boolean; feedbackOptionText: string; feedbackPopUpText: string }[] = [];
      if (hasTextOption) {
        const textOption = q.options.find(o => o.isText);
        const optionId = textOption ? Number(textOption.id) : 0;
        const feedbackId = this.getFeedbackId(q.id, textOption?.id ?? optionId);
        options.push({
          optionId,
          feedbackId: Number(feedbackId),
          status: true,
          feedbackOptionText: this.answersText[q.id] || '',
          feedbackPopUpText: ''
        });
      } else if (q.questionType === 'SINGLE' && this.answersSingle[q.id]) {
        const optionId = Number(this.answersSingle[q.id]);
        const detailsOpts = this.detailsOptionsByQuestion[q.id] || [];
        const feedbackId = detailsOpts.length > 0
          ? detailsOpts[0].feedbackId
          : this.getFeedbackId(q.id, this.answersSingle[q.id]);
        options.push({
          optionId,
          feedbackId: Number(feedbackId),
          status: true,
          feedbackOptionText: '',
          feedbackPopUpText: ''
        });
      } else if (q.questionType === 'MULTIPLE') {
        const selectedIds = new Set((this.answersMultiple[q.id] || []).map(String));
        const fullList = this.detailsOptionsByQuestion[q.id] || [];
        const optionIdsInPayload = new Set<number>();
        fullList.forEach(({ optionId, feedbackId }) => {
          optionIdsInPayload.add(optionId);
          options.push({
            optionId,
            feedbackId: Number(feedbackId),
            status: selectedIds.has(String(optionId)),
            feedbackOptionText: '',
            feedbackPopUpText: ''
          });
        });
        selectedIds.forEach(optIdStr => {
          const optionId = Number(optIdStr);
          if (!optionIdsInPayload.has(optionId)) {
            const feedbackId = this.getFeedbackId(q.id, optIdStr);
            options.push({
              optionId,
              feedbackId: Number(feedbackId),
              status: true,
              feedbackOptionText: '',
              feedbackPopUpText: ''
            });
          }
        });
      }
      return { questionId: Number(q.id), options };
    }).filter(item => item.options.length > 0);
  }



}
